import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CiLogout } from 'react-icons/ci';
import { api_url } from '../config';
import api from '../utils/api';
import { clearAuthData } from '../Redux/AuthSlice';
import { useNavigate } from 'react-router-dom';
import { MutatingDots } from 'react-loader-spinner';
import TokenSection from '../components/AdminLib/TokenSection';
import CreateProductPlan from '../components/AdminLib/CreateProductPlan';
import { FiDollarSign } from 'react-icons/fi';
import { FaMoneyBillWave, FaChartLine } from 'react-icons/fa';
import { AiOutlineAppstore } from 'react-icons/ai';

// Import dashboard components
import DashboardCard from '../components/Dashboard/DashboardCard';
import AppDistributionChart from '../components/Dashboard/AppDistributionChart';
import PricingTiersChart from '../components/Dashboard/PricingTiersChart';
import BillingFrequencyChart from '../components/Dashboard/BillingFrequencyChart';
import RecentPlansTable from '../components/Dashboard/RecentPlansTable';
import FeaturesBreakdown from '../components/Dashboard/FeaturesBreakdown';

// Skeleton components for better loading states
const CardSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm p-5 border-l-4 border-gray-200 animate-pulse">
        <div className="flex justify-between items-start">
            <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-8 bg-gray-300 rounded w-16"></div>
            </div>
            <div className="rounded-lg bg-gray-200 h-12 w-12"></div>
        </div>
        <div className="mt-4 flex items-center">
            <div className="h-4 bg-gray-200 rounded w-20"></div>
            <div className="h-4 bg-gray-200 rounded w-32 ml-2"></div>
        </div>
    </div>
);

const ChartSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm p-5 h-full animate-pulse">
        <div className="flex justify-between items-center mb-5">
            <div className="h-5 bg-gray-200 rounded w-1/3"></div>
            <div className="p-2 rounded-lg bg-gray-200 h-8 w-8"></div>
        </div>
        <div className="h-40 bg-gray-200 rounded-lg mb-4"></div>
        <div className="space-y-4">
            {[1, 2, 3].map(i => (
                <div key={i} className="space-y-2">
                    <div className="flex justify-between">
                        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                        <div className="h-4 bg-gray-200 rounded w-1/6"></div>
                    </div>
                    <div className="h-2 bg-gray-200 rounded w-full"></div>
                </div>
            ))}
        </div>
    </div>
);

const TableSkeleton = () => (
    <div className="bg-white rounded-xl shadow-sm p-5 animate-pulse">
        <div className="flex justify-between items-center mb-5">
            <div className="h-5 bg-gray-200 rounded w-1/4"></div>
            <div className="p-2 rounded-lg bg-gray-200 h-8 w-8"></div>
        </div>
        <div className="h-12 bg-gray-200 rounded-lg mb-3"></div>
        {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="h-12 bg-gray-100 rounded-lg mb-2"></div>
        ))}
    </div>
);

// Main Dashboard Component
const ProductPlanDashboard = () => {
    const { email, name, token, id } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(true);
    const [products, setProducts] = useState(null);
    const [stripeData, setStripeData] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [isActive, setActive] = useState('dashboard');
    const [animateCards, setAnimateCards] = useState(false);

    const fetchStripeData = useCallback(async () => {
        try {
            const response = await api.get(api_url + `/v1/stripe/admin/${id}/sub-data`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                }
            });
            setStripeData(response.data.data);
        } catch (error) {
            console.error('Error fetching stripe data:', error);
        }
    }, [id, token]);

    const fetchPlans = useCallback(async () => {
        const params = {
            page: 1,
            limit: 50,
        };
        try {
            const response = await api.get(api_url + `/v1/admin/${id}/plans`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`
                },
                params
            });
            setProducts(response.data.data);
        } catch (error) {
            console.error('Error fetching plans:', error);
        } finally {
            setLoading(false);
        }
    }, [id, token]);

    useEffect(() => {
        const initializeData = async () => {
            try {
                await Promise.all([fetchStripeData(), fetchPlans()]);
            } catch (error) {
                console.error('Error initializing data:', error);
            } finally {
                setLoading(false);
            }
        };

        initializeData();
    }, [fetchPlans, fetchStripeData]);

    // Animation effect when component mounts
    useEffect(() => {
        setAnimateCards(true);
        
        // Simulate loading state for smooth transitions
        const timer = setTimeout(() => {
            setLoading(false);
        }, 800);
        
        return () => clearTimeout(timer);
    }, []);

    // Calculate stats for dashboard cards
    const stats = useMemo(() => {
        if (!products) return {
            totalPlans: 0,
            totalApps: 0,
            averagePrice: 0,
            promoPlans: 0
        };
        
        return {
            totalPlans: products.length,
            totalApps: [...new Set(products.map(p => p.app))].length,
            averagePrice: products.length 
                ? Math.round(products.reduce((sum, p) => sum + Number(p.price || 0), 0) / products.length) 
                : 0,
            promoPlans: products.filter(p => p.apply_promo === 1).length
        };
    }, [products]);

    // Calculate trend (for demo, using logic based on values)
    const getTrend = (value) => {
        // This is just for demo purposes - in a real app you'd compare with historical data
        if (value % 2 === 0) return 'up';
        return 'down';
    };
    
    // Generate percentage change (for demo)
    const getPercentageChange = (value) => {
        // This is just for demo purposes - in a real app you'd calculate actual percentage changes
        return Math.floor(Math.random() * 16) - 4; // Random value between -4 and 12
    };

    const activeTab = (tab) => {
        setActive(tab);
    };

    const handleLogout = async () => {
        try {
            setLoading(true);
            await api.post(
                api_url + "/admin/logout",
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            dispatch(clearAuthData());

            // Manipulate the browser's history to prevent going back to protected pages
            window.history.pushState(null, "", "/admin-login");
            window.onpopstate = () => {
                window.history.pushState(null, "", "/admin-login");
            };

            navigate("/admin-login", { replace: true });
        } catch (error) {
            console.error("Logout error:", error);
        } finally {
            setLoading(false);
        }
    };

    // Render skeleton during initial load
    const renderDashboardContent = () => {
        if (loading) {
            return (
                <div className={`space-y-6 transition-opacity duration-500 ${animateCards ? 'opacity-100' : 'opacity-0'}`}>
                    {/* Cards Skeleton */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map(i => <CardSkeleton key={i} />)}
                    </div>
                    
                    {/* Charts Skeleton */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                        {[1, 2, 3].map(i => <ChartSkeleton key={i} />)}
                    </div>
                    
                    {/* Additional Data Skeleton */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                        <div className="lg:col-span-2">
                            <TableSkeleton />
                        </div>
                        <div>
                            <ChartSkeleton />
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                {/* Stats Cards */}
                <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 transition-all duration-700 ${animateCards ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                    <DashboardCard 
                        title="Total Plans" 
                        value={stats.totalPlans} 
                        icon={<FaChartLine size={20} className="text-blue-500" />} 
                        color="border-blue-500"
                        percentage={getPercentageChange(stats.totalPlans)}
                        trend={getTrend(stats.totalPlans)}
                        loading={false}
                    />
                    <DashboardCard 
                        title="Applications" 
                        value={stats.totalApps} 
                        icon={<AiOutlineAppstore size={20} className="text-purple-500" />} 
                        color="border-purple-500"
                        percentage={getPercentageChange(stats.totalApps)}
                        trend={getTrend(stats.totalApps)}
                        loading={false}
                    />
                    <DashboardCard 
                        title="Avg. Price" 
                        value={`$${stats.averagePrice}`} 
                        icon={<FiDollarSign size={20} className="text-green-500" />} 
                        color="border-green-500"
                        percentage={getPercentageChange(stats.averagePrice)}
                        trend={getTrend(stats.averagePrice)}
                        loading={false}
                    />
                    <DashboardCard 
                        title="Promo Plans" 
                        value={stats.promoPlans} 
                        icon={<FaMoneyBillWave size={20} className="text-orange-500" />} 
                        color="border-orange-500"
                        percentage={getPercentageChange(stats.promoPlans)}
                        trend={getTrend(stats.promoPlans)}
                        loading={false}
                    />
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
                    <AppDistributionChart products={products} loading={false} />
                    <PricingTiersChart products={products} loading={false} />
                    <BillingFrequencyChart products={products} loading={false} />
                </div>

                {/* Additional Data Row */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                    <div className="lg:col-span-2">
                        <RecentPlansTable products={products} loading={false} />
                    </div>
                    <div>
                        <FeaturesBreakdown products={products} loading={false} />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className='w-full'>
            {loading && (
                <div className="flex justify-center items-center w-full h-full bg-gradient-to-br from-opacity-40 to-opacity-60 backdrop-blur-sm fixed top-0 left-0 z-50">
                    <MutatingDots
                        visible={true}
                        height="100"
                        width="100"
                        color="#4fa94d"
                        secondaryColor="#4fa94d"
                        radius="12.5"
                        ariaLabel="mutating-dots-loading"
                    />
                </div>
            )}
            <div className='mx-10 my-10'>
                <div className='flex justify-between'>
                    <div>
                        <p className='font-semibold'>{name}</p>
                        <p>{email}</p>
                    </div>
                    <div>
                        <CiLogout onClick={handleLogout} size={28} className='text-red-500 hover:scale-110 duration-300 cursor-pointer' />
                        <p className='text-[10px]'>Logout</p>
                    </div>
                </div>
                <div className='flex mt-4 items-center gap-4'>
                    <button
                        onClick={() => activeTab('dashboard')}
                        className={`px-2 py-1 border-b hover:bg-purple-50 ${isActive === 'dashboard' ? 'border-purple-500' : ''}`}
                    >
                        Dashboard
                    </button>
                    <button
                        onClick={() => activeTab('plans')}
                        className={`px-2 py-1 border-b hover:bg-purple-50 ${isActive === 'plans' ? 'border-purple-500' : ''}`}
                    >
                        Plans
                    </button>
                    <button
                        onClick={() => activeTab('tokens')}
                        className={`px-2 py-1 border-b hover:bg-purple-50 ${isActive === 'tokens' ? 'border-purple-500' : ''}`}
                    >
                        Tokens
                    </button>
                </div>
                {isActive === 'plans' && <CreateProductPlan products={products} fetchPlans={fetchPlans} stripeData={stripeData} />}
                {isActive === 'tokens' && <TokenSection />}
            </div>
            {isActive === 'dashboard' && renderDashboardContent()}
        </div>
    );
};

export default ProductPlanDashboard;
